import type { ConciergeProductV2Detail } from '../../types';
import type { CatalogV2Offer, CatalogV2Variant } from '../../../types/catalogV2Product';

export function formatMoney(value: number): string {
  return `$${value.toFixed(2)}`;
}

/**
 * The cross-retailer offers to display for the currently-shown product. A CHILD
 * (`parentStacklineSku` set) carries its own offers at the product level; a PARENT
 * (family head, `parentStacklineSku` null) does not — its offers live on each family
 * member, so use the selected variant's. Falls back to the product-level offers when
 * no variant resolves (a standalone product, also parentless; or a parent before a
 * chip is picked, whose root offers are `[]`).
 */
export function resolveDisplayOffers(
  detail: ConciergeProductV2Detail | null | undefined,
  selectedVariant?: CatalogV2Variant,
): CatalogV2Offer[] {
  if (!detail) return [];
  const isChild = detail.parentStacklineSku != null;
  return isChild ? detail.offers ?? [] : selectedVariant?.offers ?? detail.offers ?? [];
}

/** The product fields that decide which other-store rows the panel shows. */
export type StoreOfferPolicy = Pick<
  ConciergeProductV2Detail,
  'checkoutEligible' | 'whereToBuyFallback' | 'whereToBuyOfferRetailerIds' | 'isAvailable' | 'retailPrice'
>;

/**
 * Which other-store offers get a "View • $price" row. Two rules must BOTH allow a row.
 *
 * Rule B — "Link out" is the gate when checkout is on. If checkout is on for this
 * product and "Link out" is off, the panel shows no store rows. The shopper buys
 * here, with "Add to bag". The server forces "Link out" to false when checkout is
 * off, so this rule applies only under checkout. Without that limit, a brand with
 * no checkout could never show a store row.
 *
 * Rule A — the same rule the product page uses (`offersToShow` in
 * brandclub-checkout-shop-app, `productComposer.offersToShow` in pages-backend):
 *  - Offers from retailers with "Online" on show.
 *  - If no offer is "Online", every offer shows only when "Link out" is on AND the
 *    product cannot go in the bag.
 *  - If the server sent no "Online" list, every offer shows. An older API does this.
 *
 * Example: PetArmor has checkout on, "Link out" off, and no "Online" retailer.
 * So rule B hides every row.
 */
export function storeOffersToShow(foreign: CatalogV2Offer[], policy: StoreOfferPolicy): CatalogV2Offer[] {
  const checkoutOn = policy.checkoutEligible === true;
  const linkOutOn = policy.whereToBuyFallback === true;

  // Rule B.
  if (checkoutOn && !linkOutOn) return [];

  // Rule A.
  if (!policy.whereToBuyOfferRetailerIds) return foreign;
  const online = new Set(policy.whereToBuyOfferRetailerIds);
  const onlineOffers = foreign.filter((offer) => offer.retailerId != null && online.has(offer.retailerId));
  if (onlineOffers.length) return onlineOffers;
  const canGoInBag = checkoutOn && policy.isAvailable !== false && (policy.retailPrice ?? 0) > 0;
  return linkOutOn && !canGoInBag ? foreign : [];
}

/**
 * True when the retailerId is a real marketplace retailer (Amazon, Target, …) rather than a
 * DTC/Shopify store id (>= 100000) or missing. Marketplace products are sold *at a retailer*,
 * so the seller shown should be that retailer — not the brand's own store.
 */
export function isMarketplaceRetailer(retailerId?: number): boolean {
  return !!retailerId && retailerId > 0 && retailerId < 100000;
}

/** Circular retailer mark from the media CDN; null for DTC/Shopify retailer ids or missing ids. */
export function retailerLogoUrl(retailerId?: number): string | null {
  return isMarketplaceRetailer(retailerId) ? `https://media.brandclub.com/retailer_logo/${retailerId}.webp` : null;
}
